// Google Identity Services — sign-in without the Firebase SDK.
//
// Why this replaces signInWithPopup
// ---------------------------------
// Firebase was sitting between the user and Google for no benefit we use. It
// minted its own ID token around Google's, which the backend then verified
// with firebase_admin, which then called Firebase again to read the email back
// out. Nothing here identifies a user by Firebase UID — accounts are keyed on
// email — so the entire detour produced a value we already had.
//
// GIS hands us a Google ID token directly. The backend verifies its signature,
// issuer, audience and email_verified offline with google-auth, and no longer
// needs a service account, an 18 MB SDK, or a second Google Cloud project whose
// settings can drift out of step with the one issuing the tokens.
//
// The fallback is deliberate
// --------------------------
// This script comes from accounts.google.com, and a school network that filters
// traffic can block it — which is exactly the kind of environment this platform
// runs in. If it fails to load, or no client ID is configured, the caller falls
// back to the existing Firebase popup. An unset REACT_APP_GOOGLE_SIGNIN_CLIENT_ID
// therefore means "behave exactly as before", which is what makes this safe to
// deploy before the configuration exists.

const GIS_SRC = "https://accounts.google.com/gsi/client";

// Long enough to survive a slow school connection, short enough that a user
// staring at an empty space gets the fallback button rather than nothing.
const LOAD_TIMEOUT_MS = 8000;

let loadPromise = null;

// REACT_APP_GOOGLE_API_CLIENT_ID already exists in every environment and in
// Secrets Manager, so nothing has to be added to AWS for this to work.
// REACT_APP_GOOGLE_SIGNIN_CLIENT_ID is read first only so a deployment can
// point sign-in at a different OAuth client without disturbing the existing
// one; unset, which is the normal case, the old name is used.
export function getGoogleClientId() {
  return (
    process.env.REACT_APP_GOOGLE_SIGNIN_CLIENT_ID ||
    process.env.REACT_APP_GOOGLE_API_CLIENT_ID ||
    ""
  );
}

/**
 * Resolve once window.google.accounts.id is usable.
 *
 * index.html already includes the GIS script tag, so in the normal case this
 * only waits for it. The explicit injection covers the case where that tag was
 * removed, and the polling covers `async defer` not having finished yet.
 *
 * Rejects on timeout rather than hanging, so the caller can show the fallback.
 */
export function loadGoogleIdentity() {
  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) {
      resolve(window.google.accounts.id);
      return;
    }

    const start = Date.now();
    const poll = setInterval(() => {
      if (window.google?.accounts?.id) {
        clearInterval(poll);
        resolve(window.google.accounts.id);
      } else if (Date.now() - start > LOAD_TIMEOUT_MS) {
        clearInterval(poll);
        // Null the cached promise so a later attempt can retry rather than
        // inheriting this failure for the lifetime of the page.
        loadPromise = null;
        reject(new Error("Google Identity Services did not load"));
      }
    }, 100);

    if (!document.querySelector(`script[src="${GIS_SRC}"]`)) {
      const script = document.createElement("script");
      script.src = GIS_SRC;
      script.async = true;
      script.defer = true;
      script.onerror = () => {
        clearInterval(poll);
        loadPromise = null;
        reject(new Error("Google Identity Services failed to load"));
      };
      document.head.appendChild(script);
    }
  });

  return loadPromise;
}

// The scopes sign-in needs, and no more. Identity only: no Drive, no
// Calendar. Asking for less is what keeps this out of Google's sensitive and
// restricted scope tiers, which is the difference between a form and a
// third-party security assessment at verification time.
//
// "profile" is deliberately absent. It bought nothing: the backend's two
// verifiers both return email.lower() and read no other claim, and no screen
// reads a Google name or picture. All it did was add "Name and profile
// picture" to the consent screen, asking people to hand over data that was
// then thrown away.
const SIGNIN_SCOPES = "openid email";

let tokenClient = null;

/**
 * Prepare the Google token client. Call once; it is cached per page load.
 *
 * Returns true when sign-in can proceed through Google, false when the caller
 * should fall back to the existing Firebase popup. It never throws — a broken
 * sign-in button must not take the page down with it.
 */
export async function initGoogleSignIn(onCredential) {
  const clientId = getGoogleClientId();
  if (!clientId) return false;

  let gis;
  try {
    gis = await loadGoogleIdentity();
  } catch (error) {
    console.warn("Google sign-in unavailable, falling back:", error?.message);
    return false;
  }

  if (!window.google?.accounts?.oauth2) return false;

  try {
    tokenClient = window.google.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: SIGNIN_SCOPES,
      // Always show the account chooser.
      //
      // Without this, Google silently reuses whichever account the Chrome
      // profile is already signed into. Someone whose browser is signed in
      // with a personal address that has no account here gets the popup opened
      // and closed before they can read it, then "user does not exist" — and
      // no way to pick the address that would have worked. They are locked out
      // of a platform they have a valid account on.
      //
      // The cost is one extra click for people with a single account. That is
      // a far better trade than being unable to sign in at all.
      prompt: "select_account",
      callback: (response) => {
        if (response?.error) {
          console.warn("Google sign-in returned an error:", response.error);
          onCredential?.(null, response.error);
          return;
        }
        onCredential?.(response?.access_token || null);
      },
      error_callback: (error) => {
        // Fires when the popup is closed or blocked, which the main callback
        // never sees. Without this the user clicks, nothing happens, and there
        // is no signal anywhere — the exact failure mode that made the last
        // outage take a day to diagnose.
        console.warn("Google sign-in popup failed:", error?.type || error);
        onCredential?.(null, error?.type || "popup_failed");
      },
    });
    return true;
  } catch (error) {
    console.warn("Google sign-in could not initialise:", error?.message);
    return false;
  }
}

/**
 * Open Google's account chooser. Must be called from a click handler — a popup
 * opened outside a user gesture is blocked by every browser.
 *
 * The result arrives through the callback given to initGoogleSignIn, as an
 * OAuth access token. Unlike an ID token it carries no claims, so the server
 * asks Google who it belongs to; that is the cost of using a branded button
 * instead of Google's own, which is the only element GIS will mint an ID token
 * from.
 */
export function requestGoogleSignIn() {
  if (!tokenClient) return false;
  try {
    tokenClient.requestAccessToken();
    return true;
  } catch (error) {
    console.warn("Google sign-in request failed:", error?.message);
    return false;
  }
}
